import { useCallback, useEffect, useState } from 'react';
import { unitsApi, type UnitDef } from './domain';

// Standardeinheiten als Rückfall, bis die gruppenspezifische Liste geladen ist
// (oder offline nicht verfügbar ist). Entspricht backend/src/modules/units/defaults.ts.
export const DEFAULT_UNITS: UnitDef[] = [
  { code: 'STUECK', label: 'Stück', kind: 'COUNT', factor: 1, allowDecimal: false },
  { code: 'PACKUNG', label: 'Packung', kind: 'PACK', factor: 1, allowDecimal: false },
  { code: 'FLASCHE', label: 'Flasche', kind: 'PACK', factor: 1, allowDecimal: false },
  { code: 'DOSE', label: 'Dose', kind: 'PACK', factor: 1, allowDecimal: false },
  { code: 'BEUTEL', label: 'Beutel', kind: 'PACK', factor: 1, allowDecimal: false },
  { code: 'KG', label: 'kg', kind: 'WEIGHT', factor: 1, allowDecimal: true },
  { code: 'G', label: 'g', kind: 'WEIGHT', factor: 0.001, allowDecimal: false },
  { code: 'MG', label: 'mg', kind: 'WEIGHT', factor: 0.000001, allowDecimal: false },
  { code: 'L', label: 'l', kind: 'VOLUME', factor: 1, allowDecimal: true },
  { code: 'ML', label: 'ml', kind: 'VOLUME', factor: 0.001, allowDecimal: false },
];

export const KIND_LABEL: Record<UnitDef['kind'], string> = {
  COUNT: 'Stück',
  PACK: 'Gebinde (Packung, Flasche …)',
  WEIGHT: 'Gewicht',
  VOLUME: 'Volumen',
};

export interface UnitsApi {
  units: UnitDef[];
  label: (code: string) => string;
  allowDecimal: (code: string) => boolean;
  reload: () => Promise<void>;
}

/** Lädt die für den Besitzbereich gepflegten Einheiten. */
export function useUnits(scope: string): UnitsApi {
  const [units, setUnits] = useState<UnitDef[]>(DEFAULT_UNITS);

  const reload = useCallback(async () => {
    if (!scope) return;
    try {
      const res = await unitsApi.list(scope);
      setUnits(res.data.units);
    } catch {
      // Rückfall auf die Standardeinheiten.
    }
  }, [scope]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const find = (code: string) => units.find((u) => u.code === code) ?? DEFAULT_UNITS.find((u) => u.code === code);
  return {
    units,
    label: (code) => find(code)?.label ?? code,
    allowDecimal: (code) => find(code)?.allowDecimal ?? false,
    reload,
  };
}

// Kommaangaben (Nachkommastellen) sind nur bei Einheiten sinnvoll, für die
// das in der Einheitenpflege erlaubt wurde (Standard: kg, l). Bei den übrigen
// führte das Pfeiltasten-Stepping mit step="any" sonst zu Werten wie 2.001.
export function quantityStep(allowDecimal: boolean): number | 'any' {
  return allowDecimal ? 'any' : 1;
}

export function quantityMin(allowDecimal: boolean): number {
  return allowDecimal ? 0.001 : 1;
}

export function normalizeQuantity(value: number | string, allowDecimal: boolean): number {
  const num = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(num)) return 0;
  return allowDecimal ? num : Math.round(num);
}

/**
 * Zerlegt eine Eingabe wie „5 Orangensaft“ oder „2,5 kg Mehl“ in Menge,
 * optionale Einheit und Produktname. Ohne führende Zahl bleibt die Eingabe
 * unverändert der Name.
 */
export function parseQuantityQuery(
  input: string,
  units: UnitDef[],
  fallbackUnit?: string,
): { quantity?: number; unit?: string; name: string } {
  const text = input.trim();
  const m = /^(\d+(?:[.,]\d+)?)\s+(.+)$/.exec(text);
  if (!m) return { name: text };
  let quantity = Number(m[1].replace(',', '.'));
  let rest = m[2].trim();
  let unit: string | undefined;
  const [first, ...others] = rest.split(/\s+/);
  const match = units.find((u) => u.label.toLowerCase() === first.toLowerCase() || u.code.toLowerCase() === first.toLowerCase());
  if (match && others.length > 0) {
    unit = match.code;
    rest = others.join(' ');
  }
  const allowDecimal = units.find((u) => u.code === (unit ?? fallbackUnit))?.allowDecimal ?? false;
  quantity = normalizeQuantity(quantity, allowDecimal);
  if (quantity < quantityMin(allowDecimal)) return { name: text };
  return { quantity, unit, name: rest };
}
